/**
 * Complaints module hooks (C-2). Backed by /api/v1/complaints (real Postgres,
 * complaint.complaints). A complaint links to deviations / CAPAs / OOS via the
 * C-4 event graph (the RelatedRecords panel on the detail screen).
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { apiFetch } from '../../api/client';

export interface ComplaintHistoryRow {
  event_kind: string;
  from_status: string | null;
  to_status: string | null;
  note: string | null;
  at_utc: string;
  actor_name: string | null;
}
export interface Complaint {
  complaintId: string;
  complaintNumber: string;
  receivedDate: string;
  productName: string;
  batchNumber: string | null;
  market: string | null;
  complainant: string | null;
  category: string;
  description: string;
  criticality: string;
  isAdverseEvent: boolean;
  medicalAssessment: string | null;
  medicalAssessedAt: string | null;
  investigationSummary: string | null;
  rootCause: string | null;
  recallAssessment: string;
  recallJustification: string | null;
  status: string;
  ownerUserId: string | null;
  ownerName: string | null;
  siteId: string | null;
  siteCode: string | null;
  dueDate: string | null;
  closedAt: string | null;
  createdAt: string;
  updatedAt: string;
  history?: ComplaintHistoryRow[];
}
export interface ComplaintStats {
  total: number; open: number; critical_open: number;
  adverse_open: number; recall_flagged: number; overdue: number;
}
export interface ComplaintListResp { items: Complaint[]; total: number; stats: ComplaintStats }

export interface ComplaintFilters { status?: string; criticality?: string; category?: string }

export function useComplaintsQuery(filters: ComplaintFilters = {}): UseQueryResult<ComplaintListResp> {
  const qs = new URLSearchParams();
  if (filters.status) qs.set('status', filters.status);
  if (filters.criticality) qs.set('criticality', filters.criticality);
  if (filters.category) qs.set('category', filters.category);
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  return useQuery<ComplaintListResp>({
    queryKey: ['complaints', filters],
    queryFn: ({ signal }) => apiFetch<ComplaintListResp>(`/api/v1/complaints${suffix}`, { signal }),
    staleTime: 30_000,
  });
}
export function useComplaintDetailQuery(id: string | undefined): UseQueryResult<Complaint> {
  return useQuery<Complaint>({
    queryKey: ['complaints', 'detail', id],
    queryFn: ({ signal }) => apiFetch<Complaint>(`/api/v1/complaints/${encodeURIComponent(id!)}`, { signal }),
    enabled: !!id,
    staleTime: 30_000,
  });
}

export interface NewComplaintInput {
  productName: string; description: string; batchNumber?: string; market?: string;
  complainant?: string; category?: string; criticality?: string; isAdverseEvent?: boolean;
}
export function useCreateComplaint(): UseMutationResult<Complaint, Error, NewComplaintInput> {
  const qc = useQueryClient();
  return useMutation<Complaint, Error, NewComplaintInput>({
    mutationFn: (body) => apiFetch<Complaint>('/api/v1/complaints', { method: 'POST', body }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['complaints'] }),
  });
}
export function useComplaintTransition(
  id: string | undefined
): UseMutationResult<unknown, Error, { toStatus: string; note?: string; investigationSummary?: string; rootCause?: string }> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { toStatus: string; note?: string; investigationSummary?: string; rootCause?: string }) =>
      apiFetch(`/api/v1/complaints/${encodeURIComponent(id!)}/transition`, { method: 'POST', body }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['complaints'] }),
  });
}
export function useComplaintMedicalAssessment(
  id: string | undefined
): UseMutationResult<unknown, Error, { outcome: string }> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { outcome: string }) =>
      apiFetch(`/api/v1/complaints/${encodeURIComponent(id!)}/medical-assessment`, { method: 'POST', body }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['complaints'] }),
  });
}
export function useComplaintRecallAssessment(
  id: string | undefined
): UseMutationResult<unknown, Error, { assessment: string; justification?: string }> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { assessment: string; justification?: string }) =>
      apiFetch(`/api/v1/complaints/${encodeURIComponent(id!)}/recall-assessment`, { method: 'POST', body }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['complaints'] }),
  });
}

// Allowed status transitions (mirrors the backend FSM) for the action buttons.
export const COMPLAINT_FSM: Record<string, string[]> = {
  OPEN: ['UNDER_INVESTIGATION', 'REJECTED'],
  UNDER_INVESTIGATION: ['MEDICAL_REVIEW', 'PENDING_CLOSURE', 'REJECTED'],
  MEDICAL_REVIEW: ['UNDER_INVESTIGATION', 'PENDING_CLOSURE'],
  PENDING_CLOSURE: ['CLOSED', 'UNDER_INVESTIGATION'],
  CLOSED: [],
  REJECTED: [],
};
export const STATUS_LABEL: Record<string, string> = {
  OPEN: 'Open', UNDER_INVESTIGATION: 'Under investigation', MEDICAL_REVIEW: 'Medical review',
  PENDING_CLOSURE: 'Pending closure', CLOSED: 'Closed', REJECTED: 'Rejected',
};
export const CATEGORY_LABEL: Record<string, string> = {
  QUALITY_DEFECT: 'Quality defect', ADVERSE_EVENT: 'Adverse event', PACKAGING: 'Packaging',
  LABELING: 'Labeling', EFFICACY: 'Efficacy', FOREIGN_MATTER: 'Foreign matter', OTHER: 'Other',
};
export const RECALL_LABEL: Record<string, string> = {
  NOT_ASSESSED: 'Not assessed', NOT_REQUIRED: 'Not required',
  UNDER_EVALUATION: 'Under evaluation', RECALL_INITIATED: 'Recall initiated',
};
